import React, { useState, useEffect } from 'react';
import { B } from '../constants';
import { getTripImageByDestination } from '../lib/tripImages';
import { trips, experiences, ai, blogs, maps as mapsApi, partners as partnersApi } from '../api';
import ShareModal from '../components/ShareModal';
import ConfirmModal from '../components/ConfirmModal';
import TripDayTracker from '../components/TripDayTracker';
import MapView from '../components/MapView';
import CollaboratorsModal from '../components/CollaboratorsModal';
import FeedbackButton from '../components/FeedbackButton';
import IconButton from '../components/ui/IconButton';
import { tripLifecycle } from '../lib/tripLifecycle';
import { CloneModal } from './MyTripsScreen';
import PartnerBadge from '../components/PartnerBadge';
import ApproximateLocationNote from '../components/ApproximateLocationNote';
import { geocodeWithCache } from '../lib/geocode';
import Input from '../components/ui/Input';
import Select from '../components/ui/Select';
import BlogRenderer from '../components/BlogRenderer';

// Saved activities store map coords + the approximate-location flag in a JSON notes blob.
const notesApprox = (notes) => {
  try { return !!JSON.parse(notes)._approx; } catch { return false; }
};

const TRIP_DETAIL_RESPONSIVE_CSS = `
@media (max-width: 640px) {
  .td-hero { padding: 24px 16px !important; border-radius: 14px !important; }
  .td-hero h1 { font-size: clamp(22px, 5vw, 36px) !important; }
  .td-hero p { font-size: 15px !important; }
  .td-hero-actions { gap: 8px !important; }
  .td-hero-actions button, .td-hero-actions a { font-size: 12px !important; padding: 6px 10px !important; }
  .td-tabs { flex-wrap: wrap !important; gap: 6px !important; }
  .td-tabs button { padding: 8px 14px !important; font-size: 12px !important; }
  .td-exp-form-grid { grid-template-columns: 1fr !important; }
  .td-section { padding: 16px !important; }
}
`;

const TABS = [
  { id: 'info', label: 'Info', emoji: 'ℹ️' },
  { id: 'itinerary', label: 'Itinerary', emoji: '🗓️' },
  { id: 'track', label: 'Track', emoji: '👣' },
  { id: 'map', label: 'Map', emoji: '🗺️' },
];

export default function TripDetailScreen({ trip, goScreen, showToast, user }) {
  const [tripData, setTripData] = useState(trip);
  const [loading, setLoading] = useState(true);
  const [showExpForm, setShowExpForm] = useState(false);
  const EMPTY_EXP = { source: 'itinerary', tripDayId: '', title: '', content: '', location: '', rating: 3 };
  const [expForm, setExpForm] = useState(EMPTY_EXP);
  const [stagedPhotos, setStagedPhotos] = useState([]); // [{ file, preview }]
  const [savingExp, setSavingExp] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [generatingBlog, setGeneratingBlog] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [showCollabModal, setShowCollabModal] = useState(false);

  const [expFilter, setExpFilter] = useState('all');
  const [editingExp, setEditingExp] = useState(null);
  const [editExpForm, setEditExpForm] = useState({ type: 'FREEFORM', title: '', content: '', location: '', rating: 3 });
  // A public share link (no trip id) shows only the itinerary and the map.
  const [activeTab, setActiveTab] = useState(trip?.id ? 'info' : 'itinerary');
  const [mapKey, setMapKey] = useState(0);
  useEffect(() => {
    if (activeTab === 'map') setMapKey(k => k + 1);
  }, [activeTab]);
  const [dayLogs, setDayLogs] = useState([]);
  const [selectedDay, setSelectedDay] = useState(1);
  const [logForm, setLogForm] = useState({ steps: '', distance: '', notes: '', photos: [] });
  const [savingLog, setSavingLog] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { type: 'experience'|'trip', id: number }
  const [mapLocations, setMapLocations] = useState([]);
  const [geocoding, setGeocoding] = useState(false);
  const [optimizingDay, setOptimizingDay] = useState(null);
  const [partners, setPartners] = useState([]);

  useEffect(() => {
    if (trip?.id) {
      trips.get(trip.id)
        .then(setTripData)
        .catch((err) => { console.error(err); setLoadError(true); })
        .finally(() => setLoading(false));
    } else {
      // Public /trip/:shareSlug view: the payload is already complete (and has no id).
      setLoading(false);
    }
    partnersApi.list().then(res => setPartners(res.partners)).catch(() => {});
  }, [trip?.id]);

  useEffect(() => {
    if (activeTab === 'track' && tripData?.id && tripData?.role === 'OWNER') {
      trips.getLogs(tripData.id).then(setDayLogs).catch(console.error);
    }
  }, [activeTab, tripData?.id, tripData?.role]);

  useEffect(() => {
    if (activeTab === 'track' && dayLogs.length > 0) {
      const log = dayLogs.find(l => l.dayNumber === selectedDay);
      if (log) {
        setLogForm({ steps: log.steps || '', distance: log.distance || '', notes: log.notes || '', photos: log.photos || [] });
      } else {
        setLogForm({ steps: '', distance: '', notes: '', photos: [] });
      }
    }
  }, [activeTab, selectedDay, dayLogs]);

  // Geocode itinerary locations when map tab is active
  useEffect(() => {
    if (activeTab !== 'map' || !tripData) return;
    
    const extractLocations = async () => {
      setGeocoding(true);
      setMapLocations([]);
      
      const coords = [];
      const toGeocode = [];
      
      function tryParseNotes(activity) {
        if (!activity.notes) return null;
        try {
          const parsed = JSON.parse(activity.notes);
          if (parsed && typeof parsed._lat === 'number' && typeof parsed._lng === 'number') {
            return { lat: parsed._lat, lng: parsed._lng, photoUrl: parsed.photoUrl || '', description: parsed.description || '' };
          }
        } catch {}
        return null;
      }
      
      if (tripData.days && Array.isArray(tripData.days)) {
        for (const day of tripData.days) {
          if (day.activities) {
            for (const activity of day.activities) {
              const notesData = tryParseNotes(activity);
              if (notesData) {
                coords.push({
                  lat: notesData.lat,
                  lng: notesData.lng,
                  name: activity.name || '',
                  photoUrl: notesData.photoUrl || '',
                  dayNumber: day.dayNumber,
                  description: notesData.description || '',
                });
              } else {
                const locText = activity.name || activity.location || '';
                if (locText) {
                  toGeocode.push({
                    name: activity.name || '',
                    location: locText,
                    dayNumber: day.dayNumber,
                    description: activity.notes || '',
                  });
                }
              }
            }
          }
        }
      }
      
      // Geocode text-based locations sequentially to respect Nominatim rate limit
      for (const loc of toGeocode) {
        const result = await geocodeWithCache(loc.location || loc.name, tripData.destination);
        if (result) {
          coords.push({ ...result, dayNumber: loc.dayNumber, title: loc.name, description: loc.description });
        }
      }
      
      // If still nothing, fall back to destination
      if (coords.length === 0 && tripData.destination) {
        const destResult = await geocodeWithCache(tripData.destination);
        if (destResult) {
          coords.push({ ...destResult, title: tripData.destination, dayNumber: 0, description: 'Main destination' });
        }
      }
      
      setMapLocations(coords);
      setGeocoding(false);
    };
    
    extractLocations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, tripData?.id, tripData?.destination]);

  // Ask the backend for an optimized visiting order for a single day. The
  // endpoint geocodes activities that lack coordinates, reorders them (Google
  // Directions when configured, else nearest-neighbour), and persists both, so
  // we just reload the trip and refresh the map afterwards.
  const handleOptimizeDay = async (dayId) => {
    setOptimizingDay(dayId);
    try {
      const res = await mapsApi.optimizeTripDay(dayId);
      if (!res.optimized) {
        showToast(res.message || 'Add at least two activities with locations to optimize.', 'error');
        return;
      }
      const km = res.totalDistanceMeters ? (res.totalDistanceMeters / 1000).toFixed(1) : null;
      const by = res.provider === 'google' ? 'Google Maps' : 'distance';
      showToast(`Route optimized by ${by}${km ? ` · ${km} km` : ''} 🗺️`);
      const updated = await trips.get(tripData.id);
      setTripData(updated);
      setMapKey((k) => k + 1);
    } catch (err) {
      showToast(err.message || 'Failed to optimize route', 'error');
    } finally {
      setOptimizingDay(null);
    }
  };

  const uploadPhoto = async (experienceId, file) => {
    const { uploadUrl, key } = await experiences.getUploadUrl(experienceId, file.type, file.name);
    const put = await fetch(uploadUrl, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } });
    if (!put.ok) throw new Error('Upload failed');
    await experiences.confirmPhoto(experienceId, 'new', key);
  };

  const stagePhotos = (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f.type && f.type.startsWith('image/'));
    if (!files.length) return;
    setStagedPhotos((cur) => [...cur, ...files.map((file) => ({ file, preview: URL.createObjectURL(file) }))]);
  };

  const unstagePhoto = (idx) => {
    setStagedPhotos((cur) => {
      const next = [...cur];
      const [gone] = next.splice(idx, 1);
      if (gone) URL.revokeObjectURL(gone.preview);
      return next;
    });
  };

  const resetExpForm = () => {
    stagedPhotos.forEach((p) => URL.revokeObjectURL(p.preview));
    setStagedPhotos([]);
    setExpForm(EMPTY_EXP);
  };

  // "From itinerary": picking a day pre-fills title/location from that day and links tripDayId.
  const pickItineraryDay = (dayId) => {
    const day = (tripData.days || []).find((d) => String(d.id) === String(dayId));
    if (!day) { setExpForm({ ...expForm, tripDayId: '' }); return; }
    const firstAct = (day.activities || [])[0];
    setExpForm({
      ...expForm,
      tripDayId: String(day.id),
      title: day.title || `Day ${day.dayNumber}`,
      location: (firstAct && (firstAct.location || firstAct.name)) || tripData.destination || '',
    });
  };

  const handleAddExperience = async (e) => {
    e.preventDefault();
    if (!expForm.title) {
      showToast('Title is required', 'error');
      return;
    }
    if (expForm.source === 'itinerary' && !expForm.tripDayId) {
      showToast('Pick a day from the itinerary', 'error');
      return;
    }
    setSavingExp(true);
    try {
      const fromItinerary = expForm.source === 'itinerary';
      const exp = await experiences.create({
        tripId: tripData.id,
        type: fromItinerary ? 'PLANNED' : 'FREEFORM',
        tripDayId: fromItinerary ? parseInt(expForm.tripDayId, 10) : null,
        title: expForm.title,
        content: expForm.content,
        location: expForm.location,
        rating: expForm.rating,
      });
      let failed = 0;
      for (const { file } of stagedPhotos) {
        try { await uploadPhoto(exp.id, file); } catch { failed += 1; }
      }
      const list = stagedPhotos.length ? await experiences.list(tripData.id) : [...(tripData.experiences || []), exp];
      setTripData((cur) => ({ ...cur, experiences: list }));
      setShowExpForm(false);
      resetExpForm();
      showToast(failed ? `Experience added, but ${failed} photo(s) failed to upload` : 'Experience added!', failed ? 'error' : undefined);
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setSavingExp(false);
    }
  };

  const handleDeleteExperience = async (expId) => {
    setDeleteConfirm({ type: 'experience', id: expId });
  };

  const confirmDeleteExperience = async () => {
    const expId = deleteConfirm?.id;
    if (!expId) return;
    try {
      await experiences.delete(expId);
      setTripData({ ...tripData, experiences: tripData.experiences.filter(e => e.id !== expId) });
      showToast('Experience deleted');
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setDeleteConfirm(null);
    }
  };

  const handleEditExperience = (exp) => {
    setEditingExp(exp.id);
    setEditExpForm({
      type: exp.type,
      title: exp.title,
      content: exp.content || '',
      location: exp.location || '',
      rating: exp.rating || 3
    });
  };

  const handleUpdateExperience = async (expId) => {
    try {
      const updated = await experiences.update(expId, editExpForm);
      setTripData({
        ...tripData,
        experiences: tripData.experiences.map(e => e.id === expId ? updated : e)
      });
      setEditingExp(null);
      showToast('Experience updated!');
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const handleGenerateBlog = async () => {
    if (!tripData.experiences || tripData.experiences.length === 0) {
      showToast('Add some experiences first before generating a blog!', 'error');
      return;
    }
    setGeneratingBlog(true);
    try {
      const { blog } = await ai.generateBlog(tripData.id);
      setTripData({ ...tripData, blog });
      showToast('Blog generated!');
    } catch (err) {
      console.error('Blog generation error:', err);
      showToast(err.message || 'Failed to generate blog. Make sure you have experiences and try again.', 'error');
    } finally {
      setGeneratingBlog(false);
    }
  };

  const handlePhotoUpload = async (experienceId, file) => {
    try {
      await uploadPhoto(experienceId, file);
      const updated = await experiences.list(tripData.id);
      setTripData({ ...tripData, experiences: updated });
      showToast('Photo uploaded!');
    } catch (err) {
      showToast('Failed to upload photo', 'error');
    }
  };

  const handleSaveDayLog = async () => {
    setSavingLog(true);
    try {
      const data = {
        dayNumber: selectedDay,
        steps: logForm.steps ? parseInt(logForm.steps) : null,
        distance: logForm.distance ? parseFloat(logForm.distance) : null,
        notes: logForm.notes || null,
        photos: logForm.photos || [],
      };
      const saved = await trips.saveLog(tripData.id, data);
      setDayLogs(prev => {
        const exists = prev.find(l => l.dayNumber === selectedDay);
        if (exists) return prev.map(l => l.dayNumber === selectedDay ? saved : l);
        return [...prev, saved];
      });
      showToast('Day log saved!');
    } catch (err) {
      showToast(err.message || 'Failed to save log', 'error');
    } finally {
      setSavingLog(false);
    }
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>Loading...</div>;
  if (loadError && !tripData?.title) return <div style={{ padding: 40, textAlign: 'center' }}>Trip not found</div>;

  // Role from GET /api/trips/:id; public share views carry role 'PUBLIC'.
  const role = tripData.role || (user && tripData.userId === user.id ? 'OWNER' : 'PUBLIC');
  const isOwner = role === 'OWNER';
  const isMember = ['OWNER', 'EDITOR', 'COMMENTER', 'VIEWER'].includes(role);
  const canEditContent = isOwner || role === 'EDITOR';
  const completed = tripLifecycle(tripData) === 'COMPLETED';
  // Completed trips are read-only (backend 403s); experiences stay open since they're memories.
  const canEditTrip = canEditContent && !completed && !tripData.deletedAt;
  const canEditExp = (exp) => canEditContent && user && exp.userId === user.id;
  const editTrip = () => {
    if (tripData.source === 'AI') goScreen('ai', { editTripId: tripData.id });
    else goScreen('manualPlanner', { editTripId: tripData.id });
  };
  const visibleTabs = tripData?.id
    ? TABS.filter((t) => t.id !== 'track' || isOwner)
    : TABS.filter((t) => t.id === 'itinerary' || t.id === 'map');
  const dayLabel = (dayId) => {
    const d = (tripData.days || []).find((x) => x.id === dayId);
    return d ? `D${d.dayNumber}${d.title ? ` - ${d.title}` : ''}` : null;
  };

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '40px 20px' }}>
      <style>{TRIP_DETAIL_RESPONSIVE_CSS}</style>
      <button onClick={() => goScreen('trips')} aria-label="Back to My Trips" style={{ background: 'none', border: 'none', color: B.muted, cursor: 'pointer', marginBottom: 16, fontSize: 14 }}>
        ← Back to My Trips
      </button>

      <div className="td-hero" style={{ background: `linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.6)), url(${tripData.coverImage || 'https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=1200'}) center/cover`, borderRadius: 20, padding: 40, color: 'white', marginBottom: 32 }}>
        <h1 style={{ fontFamily: "'Playfair Display',serif", fontSize: 36, marginBottom: 8 }}>{tripData.title}</h1>
        <p style={{ fontSize: 18, opacity: 0.9, marginBottom: 16 }}>{tripData.destination}</p>
        <div className="td-hero-actions" style={{ display: 'flex', gap: 12, fontSize: 14, opacity: 0.8, flexWrap: 'wrap', alignItems: 'center' }}>
          {tripData.startDate && <span>📅 {new Date(tripData.startDate).toLocaleDateString()} - {tripData.endDate ? new Date(tripData.endDate).toLocaleDateString() : 'TBD'}</span>}
          {tripData.budget && <span>💰 ${tripData.budget}</span>}
          {tripData.isPublic && <span>🌐 Public</span>}
          {role !== 'OWNER' && isMember && <span>👥 {role.charAt(0) + role.slice(1).toLowerCase()}</span>}
          {isOwner && <button onClick={() => setShowShareModal(true)} style={{ background: B.sky, color: 'white', border: 'none', padding: '8px 14px', borderRadius: 20, cursor: 'pointer', fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap' }}>
            🔗 Share
          </button>}
          {canEditTrip && <button onClick={editTrip} aria-label="Edit trip" style={{ background: B.amber, color: B.night, border: 'none', padding: '8px 14px', borderRadius: 20, cursor: 'pointer', fontWeight: 700, fontSize: 13, whiteSpace: 'nowrap' }}>
            ✏️ Edit
          </button>}
          {isOwner && completed && !tripData.deletedAt && <button onClick={() => setShowCloneModal(true)} aria-label="Clone trip" style={{ background: 'white', color: B.forest, border: 'none', padding: '8px 14px', borderRadius: 20, cursor: 'pointer', fontWeight: 700, fontSize: 13, whiteSpace: 'nowrap' }}>
            📋 Clone
          </button>}
          {isMember && <button onClick={() => setShowCollabModal(true)} style={{ background: B.forest, color: 'white', border: 'none', padding: '8px 14px', borderRadius: 20, cursor: 'pointer', fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap' }}>
            👥 Collaborators
          </button>}
          {isMember && <FeedbackButton context="trip" tripId={tripData.id} destination={tripData.destination} style={{ background: 'rgba(255,255,255,0.18)', color: 'white', border: '1px solid rgba(255,255,255,0.5)', padding: '8px 14px', borderRadius: 20, cursor: 'pointer', fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap' }} />}
        </div>
      </div>

      <div className="td-tabs" style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
        {visibleTabs.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{ padding: '10px 20px', borderRadius: 50, border: 'none', background: activeTab === tab.id ? B.forest : '#f0f0f0', color: activeTab === tab.id ? 'white' : B.night, cursor: 'pointer', fontWeight: 600, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
            {tab.emoji} {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'info' && partners.length > 0 && (
        <div style={{ background: 'white', borderRadius: 16, padding: 24, marginBottom: 24, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
          <h3 style={{ fontFamily: "'Montserrat',sans-serif", fontWeight: 700, marginBottom: 16 }}>Travel Partners</h3>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {partners.map(p => (
              <PartnerBadge
                key={p.slug}
                partner={p}
                destination={tripData.destination}
                origin={tripData.days?.[0]?.activities?.[0]?.location || undefined}
                startDate={tripData.startDate ? new Date(tripData.startDate).toISOString().slice(0, 10) : undefined}
                endDate={tripData.endDate ? new Date(tripData.endDate).toISOString().slice(0, 10) : undefined}
                compact
              />
            ))}
          </div>
        </div>
      )}

      {activeTab === 'info' && (
        <div style={{ background: 'white', borderRadius: 16, padding: 24, marginBottom: 24, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, gap: 8, flexWrap: 'wrap' }}>
            <h3 style={{ fontFamily: "'Montserrat',sans-serif", fontWeight: 700 }}>Experiences</h3>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <Select
                value={expFilter}
                onChange={(v) => setExpFilter(v)}
                options={[
                  { value: 'all', label: 'All' },
                  { value: 'PLANNED', label: 'From itinerary' },
                  { value: 'FREEFORM', label: 'Own' },
                ]}
                size="sm"
                style={{ minWidth: 120 }}
              />
              {canEditContent && (
                <button onClick={() => { if (showExpForm) resetExpForm(); setShowExpForm(!showExpForm); }} style={{ background: B.forest, color: 'white', border: 'none', padding: '6px 12px', borderRadius: 16, cursor: 'pointer', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>
                  {showExpForm ? 'Close' : '+ Add'}
                </button>
              )}
            </div>
          </div>

          {showExpForm && canEditContent && (
            <form onSubmit={handleAddExperience} style={{ background: '#f9f9f9', padding: 16, borderRadius: 12, marginBottom: 16 }}>
              <div role="radiogroup" aria-label="Experience source" style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                {[{ id: 'itinerary', label: '🗓️ From itinerary' }, { id: 'own', label: '✍️ Own experience' }].map((o) => (
                  <button
                    key={o.id}
                    type="button"
                    role="radio"
                    aria-checked={expForm.source === o.id}
                    onClick={() => setExpForm({ ...EMPTY_EXP, rating: expForm.rating, content: expForm.content, source: o.id })}
                    style={{ padding: '8px 14px', borderRadius: 20, border: `1px solid ${expForm.source === o.id ? B.forest : '#ddd'}`, background: expForm.source === o.id ? B.forest : 'white', color: expForm.source === o.id ? 'white' : B.night, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
              {expForm.source === 'itinerary' && (
                (tripData.days || []).length > 0 ? (
                  <div style={{ marginBottom: 12 }}>
                    <Select
                      value={expForm.tripDayId}
                      onChange={(v) => pickItineraryDay(v)}
                      placeholder="Pick a day from the itinerary"
                      options={(tripData.days || []).map((d) => ({ value: String(d.id), label: `D${d.dayNumber} - ${d.title || `Day ${d.dayNumber}`}` }))}
                      size="md"
                      fullWidth
                    />
                  </div>
                ) : (
                  <p style={{ fontSize: 13, color: B.muted, marginBottom: 12 }}>This trip has no itinerary days yet — add an own experience instead.</p>
                )
              )}
              <div className="td-exp-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                <Input style={{ padding: 10, borderRadius: 8, border: '1px solid #ddd' }} value={expForm.title} onChange={e => setExpForm({ ...expForm, title: e.target.value })} placeholder="Experience title *" />
                <Input style={{ padding: 10, borderRadius: 8, border: '1px solid #ddd' }} value={expForm.location} onChange={e => setExpForm({ ...expForm, location: e.target.value })} placeholder="Location" />
              </div>
              <textarea style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #ddd', marginBottom: 12, minHeight: 60, boxSizing: 'border-box' }} value={expForm.content} onChange={e => setExpForm({ ...expForm, content: e.target.value })} placeholder="Describe your experience..." />
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <span>Rating:</span>
                {[1,2,3,4,5].map(r => (
                  <span key={r} onClick={() => setExpForm({ ...expForm, rating: r })} style={{ cursor: 'pointer', fontSize: 18, color: r <= expForm.rating ? B.amber : '#ddd' }}>★</span>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
                {stagedPhotos.map((p, i) => (
                  <div key={p.preview} style={{ position: 'relative' }}>
                    <img src={p.preview} alt={`Selected ${i + 1}`} style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 8, display: 'block' }} />
                    <button type="button" onClick={() => unstagePhoto(i)} aria-label={`Remove photo ${i + 1}`} style={{ position: 'absolute', top: -6, right: -6, width: 20, height: 20, borderRadius: '50%', border: 'none', background: B.night, color: 'white', cursor: 'pointer', fontSize: 12, lineHeight: '20px', padding: 0 }}>×</button>
                  </div>
                ))}
                <label style={{ cursor: 'pointer', background: '#f0f0f0', padding: '8px 12px', borderRadius: 8, fontSize: 12 }}>
                  📷 {stagedPhotos.length ? 'Add more photos' : 'Add photos'}
                  <input type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={(e) => { stagePhotos(e.target.files); e.target.value = ''; }} />
                </label>
              </div>
              <button type="submit" disabled={savingExp} style={{ background: B.forest, color: 'white', border: 'none', padding: '10px 20px', borderRadius: 8, cursor: savingExp ? 'wait' : 'pointer', fontWeight: 600, opacity: savingExp ? 0.7 : 1 }}>
                {savingExp ? (stagedPhotos.length ? 'Saving & uploading…' : 'Saving…') : 'Save Experience'}
              </button>
            </form>
          )}

          {(() => {
            const filtered = (expFilter === 'all'
              ? tripData.experiences
              : tripData.experiences?.filter(e => e.type === expFilter)) || [];
            if (filtered.length === 0) {
              return <p style={{ color: B.muted, textAlign: 'center', padding: 20 }}>No experiences yet.</p>;
            }
            return filtered.map(exp => (
              <div key={exp.id} style={{ borderBottom: '1px solid #eee', padding: 16, marginBottom: 8 }}>
                {editingExp === exp.id ? (
                  <div>
                    <div className="td-exp-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                      <Input style={{ padding: 8, borderRadius: 6, border: '1px solid #ddd' }} value={editExpForm.title} onChange={e => setEditExpForm({ ...editExpForm, title: e.target.value })} placeholder="Title" />
                      <Input style={{ padding: 8, borderRadius: 6, border: '1px solid #ddd' }} value={editExpForm.location} onChange={e => setEditExpForm({ ...editExpForm, location: e.target.value })} placeholder="Location" />
                    </div>
                    <textarea style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #ddd', marginBottom: 8, boxSizing: 'border-box' }} value={editExpForm.content} onChange={e => setEditExpForm({ ...editExpForm, content: e.target.value })} placeholder="Description" />
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 8 }}>
                      <span>Rating:</span>
                      {[1,2,3,4,5].map(r => (
                        <span key={r} onClick={() => setEditExpForm({ ...editExpForm, rating: r })} style={{ cursor: 'pointer', fontSize: 16, color: r <= editExpForm.rating ? B.amber : '#ddd' }}>★</span>
                      ))}
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={() => handleUpdateExperience(exp.id)} style={{ background: B.forest, color: 'white', border: 'none', padding: '6px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>Save</button>
                      <button onClick={() => setEditingExp(null)} style={{ background: '#ddd', color: B.night, border: 'none', padding: '6px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                      <div style={{ minWidth: 0 }}>
                        <span style={{ background: exp.type === 'PLANNED' ? B.forest : B.amber, color: 'white', padding: '2px 8px', borderRadius: 4, fontSize: 11, marginRight: 8 }}>
                          {exp.type === 'PLANNED' ? (dayLabel(exp.tripDayId) || 'Itinerary') : 'Own'}
                        </span>
                        <span style={{ fontWeight: 700 }}>{exp.title}</span>
                        {exp.location && <span style={{ color: B.muted, fontSize: 13, marginLeft: 8 }}>📍 {exp.location}</span>}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                        <div>{[1,2,3,4,5].map(r => <span key={r} style={{ color: r <= (exp.rating || 0) ? B.amber : '#ddd' }}>★</span>)}</div>
                        {canEditExp(exp) && <IconButton variant="edit" size="sm" label="Edit experience" onClick={() => handleEditExperience(exp)} />}
                        {canEditExp(exp) && <IconButton variant="delete" size="sm" label="Delete experience" onClick={() => handleDeleteExperience(exp.id)} />}
                      </div>
                    </div>
                    {exp.content && <p style={{ marginTop: 8, color: B.night, fontSize: 14 }}>{exp.content}</p>}
                    <div style={{ marginTop: 12, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      {exp.photos?.map(photo => (
                        <img key={photo.id} src={photo.url} alt="" style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 8 }} />
                      ))}
                      {canEditExp(exp) && (
                        <label style={{ cursor: 'pointer', background: '#f0f0f0', padding: '8px 12px', borderRadius: 8, fontSize: 12 }}>
                          📷 Add Photo
                          <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => e.target.files[0] && handlePhotoUpload(exp.id, e.target.files[0])} />
                        </label>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ));
          })()}
        </div>
      )}

      {activeTab === 'info' && (
        <div aria-disabled="true" style={{ background: '#f7f7f7', border: '1px dashed #d4d4d4', borderRadius: 16, padding: 24, marginBottom: 24, color: B.muted }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
            <h3 style={{ fontFamily: "'Montserrat',sans-serif", fontWeight: 700, color: B.night, opacity: 0.6 }}>💬 Trip discussion</h3>
            <span style={{ background: B.amber, color: B.night, padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700 }}>Coming soon</span>
          </div>
          <p style={{ fontSize: 14 }}>Connect with travelers going to the same places — share rides and discussions.</p>
        </div>
      )}

      {activeTab === 'itinerary' && (
        <div style={{ background: 'white', borderRadius: 16, padding: 24, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
          <h3 style={{ marginBottom: 20, fontFamily: "'Montserrat',sans-serif", fontWeight: 700 }}>🗓️ Itinerary</h3>
          {(() => {
            const optimizableDays = (tripData?.days || []).filter(
              (d) => Array.isArray(d.activities) && d.activities.length >= 2,
            );
            if (!canEditTrip || optimizableDays.length === 0) return null;
            return (
              <div style={{ marginBottom: 16 }}>
                <p style={{ fontSize: 12, color: B.muted, fontFamily: "'Montserrat', sans-serif", marginBottom: 8 }}>
                  ⚡ Optimize the visiting order of a day's activities to minimize travel:
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {optimizableDays.map((day) => (
                    <button
                      key={day.id}
                      type="button"
                      disabled={optimizingDay === day.id}
                      onClick={() => handleOptimizeDay(day.id)}
                      style={{
                        background: optimizingDay === day.id ? B.sand : B.forest,
                        color: optimizingDay === day.id ? B.muted : 'white',
                        border: 'none',
                        padding: '8px 14px',
                        borderRadius: 20,
                        cursor: optimizingDay === day.id ? 'default' : 'pointer',
                        fontWeight: 600,
                        fontSize: 12,
                        fontFamily: "'Montserrat', sans-serif",
                      }}
                    >
                      {optimizingDay === day.id ? 'Optimizing…' : `Optimize Day ${day.dayNumber} route`}
                    </button>
                  ))}
                </div>
              </div>
            );
          })()}
          {tripData.days?.length > 0 ? (
            tripData.days.map(day => (
              <div key={day.id} style={{ borderLeft: `3px solid ${B.forest}`, paddingLeft: 16, marginBottom: 24 }}>
                <div style={{ fontWeight: 700, color: B.forest, fontSize: 16, marginBottom: 4 }}>Day {day.dayNumber}: {day.title || `Day ${day.dayNumber}`}</div>
                {day.activities?.length > 0 ? day.activities.map(act => (
                  <div key={act.id} style={{ fontSize: 14, marginBottom: 8, display: 'flex', gap: 8 }}>
                    {act.time && <span style={{ color: B.muted, fontWeight: 600 }}>{act.time}</span>}
                    <div>
                      <div>{act.name}</div>
                      {act.location && <div style={{ color: B.muted, fontSize: 12 }}>{act.location}</div>}
                      <ApproximateLocationNote approximate={notesApprox(act.notes)} />
                    </div>
                  </div>
                )) : <div style={{ color: B.muted, fontSize: 13 }}>No activities</div>}
              </div>
            ))
          ) : <p style={{ color: B.muted, textAlign: 'center', padding: 20 }}>No itinerary</p>}
        </div>
      )}

      {activeTab === 'track' && (
        <TripDayTracker
          days={tripData.days}
          dayLogs={dayLogs}
          selectedDay={selectedDay}
          setSelectedDay={setSelectedDay}
          logForm={logForm}
          setLogForm={setLogForm}
          onSaveLog={handleSaveDayLog}
          savingLog={savingLog}
          trip={tripData}
        />
      )}

      {activeTab === 'info' && (
      <div style={{ background: 'white', borderRadius: 16, padding: 24, marginBottom: 32, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ fontFamily: "'Montserrat',sans-serif", fontWeight: 700 }}>Blog</h3>
          <div style={{ display: 'flex', gap: 8 }}>
            {isOwner && tripData.blog && (
              <IconButton variant="edit" label="Edit blog" onClick={() => goScreen('blogEdit', tripData)} />
            )}
            {isOwner && !tripData.blog && (
              <button onClick={handleGenerateBlog} disabled={generatingBlog} style={{ background: generatingBlog ? '#ccc' : B.amber, color: B.night, border: 'none', padding: '10px 20px', borderRadius: 20, cursor: generatingBlog ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: 13 }}>
                {generatingBlog ? 'Generating...' : '✨ Generate with AI'}
              </button>
            )}
          </div>
        </div>
        
        {tripData.blog ? (
          <div>
            <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 24, marginBottom: 16 }}>{tripData.blog.title}</h2>
            <BlogRenderer content={tripData.blog.content} experiences={tripData.experiences || []} />

            {tripData.experiences?.length > 0 && (
              <div style={{ marginTop: 32 }}>
                <h4 style={{ fontFamily: "'Montserrat',sans-serif", fontWeight: 700, fontSize: 15, marginBottom: 16, color: B.night }}>📍 Places Visited</h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16 }}>
                  {tripData.experiences.map(exp => (
                    <div key={exp.id} style={{
                      background: B.sand,
                      borderRadius: 14,
                      overflow: 'hidden',
                      boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                      cursor: 'default',
                    }}
                      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 24px rgba(0,0,0,0.1)'; }}
                      onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.06)'; }}
                    >
                      {exp.photos?.[0] && (
                        <img
                    src={exp.photos[0].url}
                    alt={exp.location || exp.title}
                    style={{ width: '100%', height: 140, objectFit: 'cover' }}
                    onError={(e) => {
                      const img = e.currentTarget;
                      if (img.dataset.fallback) return;
                      img.dataset.fallback = '1';
                      img.src = getTripImageByDestination(exp.location || exp.title || '');
                    }}
                  />
                      )}
                      <div style={{ padding: 14 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                          <div>
                            <div style={{ fontWeight: 700, fontSize: 14, color: B.night }}>{exp.title}</div>
                            {exp.location && <div style={{ fontSize: 12, color: B.muted, marginTop: 2 }}>📍 {exp.location}</div>}
                          </div>
                          <div style={{ fontSize: 12, color: B.amber, whiteSpace: 'nowrap', marginLeft: 8 }}>
                            {[1,2,3,4,5].map(r => (
                              <span key={r} style={{ color: r <= (exp.rating || 0) ? B.amber : '#ddd' }}>★</span>
                            ))}
                          </div>
                        </div>
                        {exp.content && (
                          <p style={{ fontSize: 12, color: '#666', lineHeight: 1.5, marginTop: 6 }}>{exp.content}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div style={{ marginTop: 16, display: 'flex', gap: 12 }}>
              {isOwner && <button onClick={async () => {
                const willPublish = !tripData.blog.isPublished;
                const updated = await blogs.update(tripData.id, { isPublished: willPublish });
                let nextTripData = { ...tripData, blog: updated };
                if (willPublish) {
                  try {
                    const shareRes = await trips.share(tripData.id);
                    nextTripData = { ...nextTripData, isPublic: true, shareSlug: shareRes?.shareSlug || nextTripData.shareSlug };
                  } catch (err) {
                    console.error('Failed to make trip public for blog sharing:', err);
                  }
                }
                setTripData(nextTripData);
              }} style={{ background: tripData.blog.isPublished ? B.forest : '#ddd', color: 'white', border: 'none', padding: '8px 16px', borderRadius: 20, cursor: 'pointer', fontSize: 12 }}>
                {tripData.blog.isPublished ? '✅ Published' : 'Publish'}
              </button>}
              {tripData.blog?.isPublished && tripData.id && (
                <a href={`/blog/public/${tripData.id}`} target="_blank" rel="noopener noreferrer" style={{ background: '#f0f0f0', color: B.night, padding: '8px 16px', borderRadius: 20, textDecoration: 'none', fontSize: 12 }}>
                  🔗 View Public
                </a>
              )}
            </div>
          </div>
        ) : (
          <p style={{ color: B.muted, textAlign: 'center', padding: 20 }}>{isOwner ? 'No blog yet. Generate one with AI!' : 'No blog yet.'}</p>
        )}
      </div>
      )}
      {showCloneModal && (
        <CloneModal
          trip={tripData}
          showToast={showToast}
          onClose={() => setShowCloneModal(false)}
          onCloned={(clone) => { setShowCloneModal(false); goScreen('tripDetail', clone); }}
        />
      )}
      {showShareModal && (
        <ShareModal trip={tripData} onClose={() => setShowShareModal(false)} />
      )}
      {showCollabModal && (
        <CollaboratorsModal
          tripId={tripData.id}
          isOwner={isOwner}
          onClose={() => setShowCollabModal(false)}
        />
      )}
      {activeTab === 'map' && (
        <div style={{ marginBottom: 24 }}>
          {geocoding ? (
            <div style={{ height: 400, background: B.sand, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 16 }}>
              <p style={{ color: B.muted, fontFamily: "'Montserrat', sans-serif" }}>
                📍 Geocoding locations...
              </p>
            </div>
          ) : mapLocations.length > 0 ? (
            <MapView key={mapKey} locations={mapLocations} height={420} />
          ) : (
            <div style={{ height: 400, background: B.sand, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', borderRadius: 16, padding: 20 }}>
              <p style={{ color: B.muted, fontFamily: "'Montserrat', sans-serif", marginBottom: 12 }}>
                🗺️ No locations found to display
              </p>
              <p style={{ color: '#999', fontSize: 12, fontFamily: "'Montserrat', sans-serif", textAlign: 'center' }}>
                Add activities to your itinerary to see them on the map
              </p>
            </div>
          )}
        </div>
      )}

      {deleteConfirm?.type === 'experience' && (
        <ConfirmModal
          open={true}
          title="Delete Experience"
          message="Are you sure you want to delete this experience? This action cannot be undone."
          confirmText="Delete"
          cancelText="Cancel"
          onConfirm={confirmDeleteExperience}
          onCancel={() => setDeleteConfirm(null)}
        />
      )}
    </div>
  );
}